/**
 * Thin wrappers around the Kyoto workflow endpoints introduced in
 * sub-project A + B. Mirrors the conventions of denver/src/copilot/apiClient.js.
 *
 * - getWorkflows()            → GET    /api/copilot/v1/workflows
 * - getWorkflow(id)           → GET    /api/copilot/v1/workflows/{id}
 * - renderRunBrief(id, params)→ POST   /api/copilot/v1/workflows/{id}/run-brief
 *
 * Uses the same BASE URL derivation as apiClient.js (app-<env> / dash-<env>
 * → copilot-<env>) so dev hosts resolve correctly.
 */
import { BASE, csrfToken, authHeader } from "../apiClient";

async function getJson(path) {
  const r = await fetch(`${BASE}${path}`, {
    method: "GET",
    credentials: "include",
    headers: { "Accept": "application/json", ...authHeader() },
  });
  const body = await r.json().catch(() => null);
  if (r.status !== 200) {
    const code = (body && body.error) || `http_${r.status}`;
    throw new Error(`GET ${path} failed: ${code}`);
  }
  return body;
}

export async function getWorkflows() {
  return getJson("/workflows");
}

// Recent-first variant, used by the chat panel's welcome surface to
// surface workflows the caller ran most recently. Server-side sort is
// `last_run_at DESC NULLS LAST, name`; the resulting rows carry a
// `last_run_at` ISO string (or null). The `limit` keeps the welcome
// list short — full library lives behind the "See all" link.
export async function getRecentWorkflows(limit = 5) {
  return getJson(`/workflows?sort=recent&limit=${encodeURIComponent(limit)}`);
}

export async function getWorkflow(id) {
  return getJson(`/workflows/${id}`);
}

export async function renderRunBrief(workflowId, params) {
  const r = await fetch(`${BASE}/workflows/${workflowId}/run-brief`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": csrfToken(),
      ...authHeader(),
    },
    body: JSON.stringify({ params }),
  });
  const body = await r.json().catch(() => null);
  if (r.status === 200) return body.brief;
  if (r.status === 400 && body && Array.isArray(body.errors)) {
    const err = new Error("run-brief validation failed");
    err.errors = body.errors;
    throw err;
  }
  if (r.status === 404) throw new Error("not_found");
  throw new Error(`run-brief failed: HTTP ${r.status}`);
}

/**
 * Create a private end-user workflow (D.2's new endpoint).
 * POST /api/copilot/v1/workflows
 * Body: { name, summary?, playbook }
 *
 * Visibility is forced to 'private' server-side. Throws Error with .code
 * matching the server's error code on non-2xx ({name_conflict,
 * invalid_playbook, invalid_field, ...}).
 */
export async function createWorkflow({ name, summary, playbook }) {
  const r = await fetch(`${BASE}/workflows`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": csrfToken(),
      ...authHeader(),
    },
    body: JSON.stringify({ name, summary, playbook }),
  });
  const body = await r.json().catch(() => null);
  if (r.status >= 200 && r.status < 300) return body;
  const code = (body && body.error) || `http_${r.status}`;
  const err = new Error(`createWorkflow failed: ${code}`);
  err.code = code;
  if (body && body.message) err.message_text = body.message;
  if (body && Array.isArray(body.errors)) err.errors = body.errors;
  throw err;
}

/**
 * Edit a workflow the caller owns (D.3).
 * PATCH /api/copilot/v1/workflows/{id}
 * Body: { name?, summary?, playbook? } — visibility stays private server-side.
 * Same error-shaping as createWorkflow ({name_conflict, invalid_playbook, ...}).
 */
export async function updateWorkflow(id, { name, summary, playbook }) {
  const r = await fetch(`${BASE}/workflows/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": csrfToken(),
      ...authHeader(),
    },
    body: JSON.stringify({ name, summary, playbook }),
  });
  const body = await r.json().catch(() => null);
  if (r.status >= 200 && r.status < 300) return body;
  const code = (body && body.error) || `http_${r.status}`;
  const err = new Error(`updateWorkflow failed: ${code}`);
  err.code = code;
  if (body && body.message) err.message_text = body.message;
  if (body && Array.isArray(body.errors)) err.errors = body.errors;
  throw err;
}

/**
 * Delete a workflow the caller owns (D.3). DELETE /api/copilot/v1/workflows/{id}
 * Owner-only server-side (404 otherwise). Throws Error with .code on non-2xx.
 */
export async function deleteWorkflow(id) {
  const r = await fetch(`${BASE}/workflows/${id}`, {
    method: "DELETE",
    credentials: "include",
    headers: { "X-CSRFToken": csrfToken(), ...authHeader() },
  });
  const body = await r.json().catch(() => null);
  if (r.status >= 200 && r.status < 300) return body;
  const code = (body && body.error) || `http_${r.status}`;
  const err = new Error(`deleteWorkflow failed: ${code}`);
  err.code = code;
  throw err;
}
